import React, { createContext, useContext, ReactNode, useCallback, useEffect, useState } from 'react';

// Constants for local storage keys to ensure consistency
export const STORAGE_KEYS = {
  VIEW_MODE: 'app_viewMode',
  THEME: 'app_theme',
  PIN_SETUP_STATUS: 'app_pinSetupStatus',
};

// PIN setup status values
export const PIN_SETUP_STATUS = {
  NEEDED: 'needed',
  COMPLETED: 'completed',
};

// Custom hook that wraps useLocalStorage with consistent serialization and error handling
function useLocalStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = useState<T>(initialValue);

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const item = localStorage.getItem(key);
      if (item === null) {
        setStoredValue(initialValue);
        return;
      }
      try {
        setStoredValue(JSON.parse(item));
      } catch {
        // Non-JSON primitives
        const primitive: any =
          item === 'true' ? true :
          item === 'false' ? false :
          !isNaN(Number(item)) ? Number(item) : item;
        setStoredValue(primitive as T);
      }
    } catch (error) {
      // Failed to initialize localStorage key, using default value
      setStoredValue(initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const setValue = (value: T) => {
    try {
      setStoredValue(value);
      if (typeof window !== 'undefined') {
        const valueToStore = typeof value === 'object' ? JSON.stringify(value) : String(value ?? '');
        localStorage.setItem(key, valueToStore);
      }
    } catch (error) {
      // Failed to set localStorage key, continuing with state update
    }
  };

  return [storedValue, setValue] as const;
}

// Define types for our local storage values
interface LocalStorageState {
  // App preferences
  viewMode: 'table' | 'card';
  theme: 'light' | 'dark' | 'pharmacy' | 'system';
  
  // Authentication state
  pinSetupStatus: 'needed' | 'completed' | null;
  
  // Add any other values you want to persist in localStorage
}

// Define the context shape
interface LocalStorageContextType {
  // Getters
  getValue: <K extends keyof LocalStorageState>(key: K) => LocalStorageState[K] | null;
  
  // Setters
  setValue: <K extends keyof LocalStorageState>(
    key: K,
    value: LocalStorageState[K]
  ) => void;
  
  // Specific values with their setters
  viewMode: LocalStorageState['viewMode'];
  setViewMode: (mode: LocalStorageState['viewMode']) => void;
  
  theme: LocalStorageState['theme'];
  setTheme: (theme: LocalStorageState['theme']) => void;
  
  pinSetupStatus: LocalStorageState['pinSetupStatus'];
  setPinSetupStatus: (status: LocalStorageState['pinSetupStatus']) => void;
  
  // Helper methods
  clearValue: (key: keyof LocalStorageState) => void;
  needsPinSetup: () => boolean;
  syncPinSetupStatus: () => void;
}

// Create the context with a default undefined value
const LocalStorageContext = createContext<LocalStorageContextType | undefined>(undefined);

// Provider component
export const LocalStorageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Initialize our storage values
  const [viewMode, setViewMode] = useLocalStorage<LocalStorageState['viewMode']>(
    STORAGE_KEYS.VIEW_MODE, 
    'table'
  );
  
  const [theme, setTheme] = useLocalStorage<LocalStorageState['theme']>(
    STORAGE_KEYS.THEME, 
    'system'
  );
  
  const [pinSetupStatus, setPinSetupStatusInternal] = useLocalStorage<LocalStorageState['pinSetupStatus']>(
    STORAGE_KEYS.PIN_SETUP_STATUS, 
    null
  );
  
  // Function to sync PIN setup status (simplified since we removed offline PIN functionality)
  const syncPinSetupStatus = useCallback(() => {
    try {
      // Since we removed PIN functionality, always mark as completed for online-only mode
      const newStatus = PIN_SETUP_STATUS.COMPLETED;
      
      // Only update if there's a mismatch to prevent unnecessary rerenders
      if (pinSetupStatus !== newStatus) {
        setPinSetupStatusInternal(newStatus);
        
        // Also update direct localStorage for immediate consistency
        if (typeof window !== 'undefined') {
          // Store as a simple string to avoid JSON parsing issues
          localStorage.setItem(STORAGE_KEYS.PIN_SETUP_STATUS, newStatus);
        }
      }
    } catch (err) {
      // Error syncing PIN setup status, continuing
    }
  }, [pinSetupStatus, setPinSetupStatusInternal]);
  
  // Wrapped setter for pinSetupStatus that ensures consistency with localStorage
  const setPinSetupStatus = (status: LocalStorageState['pinSetupStatus']) => {
    // Update state through our hook
    setPinSetupStatusInternal(status);
    
    // Also directly update localStorage for immediate consistency across the app
    try {
      if (typeof window !== 'undefined') {
        // Store as a simple string to avoid JSON parsing issues
        const valueToStore = status || '';
        localStorage.setItem(STORAGE_KEYS.PIN_SETUP_STATUS, valueToStore);
      }
    } catch (storageErr) {
      // Error setting PIN setup status in localStorage, continuing
    }
  };
  
  // Check PIN setup status on mount to ensure it's synced
  useEffect(() => {
    syncPinSetupStatus();
    
    // Also set up an interval to periodically check PIN setup status
    // This helps maintain consistency across tabs/windows
    const syncInterval = setInterval(syncPinSetupStatus, 30000); // Check every 30 seconds
    
    return () => clearInterval(syncInterval);
  }, [syncPinSetupStatus]);
  
  // Generic getter method
  const getValue = <K extends keyof LocalStorageState>(key: K): LocalStorageState[K] | null => {
    try {
      if (typeof window === 'undefined') return null;
      const storageKey = `app_${key}`;
      const raw = localStorage.getItem(storageKey);
      if (raw == null) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return raw as unknown as LocalStorageState[K];
      }
    } catch (err) {
      // Error reading localStorage value, returning null
      return null;
    }
  };
  
  // Generic setter method
  const setValue = <K extends keyof LocalStorageState>(key: K, value: LocalStorageState[K]): void => {
    try {
      switch (key) {
        case 'viewMode':
          setViewMode(value as LocalStorageState['viewMode']);
          break;
        case 'theme':
          setTheme(value as LocalStorageState['theme']);
          break;
        case 'pinSetupStatus':
          setPinSetupStatus(value as LocalStorageState['pinSetupStatus']);
          break;
        default:
          // No setter implemented for key
      }
    } catch (err) {
      // Error setting value, continuing
    }
  };
  
  // Helper methods
  const clearValue = (key: keyof LocalStorageState): void => {
    setValue(key, null as any);
    
    // Also directly clear from localStorage
    try {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(`app_${key}`);
      }
    } catch (err) {
      // Error clearing localStorage, continuing
    }
  };
  
  const needsPinSetup = (): boolean => {
    // Since we removed PIN functionality, never needs PIN setup in online-only mode
    return false;
  };
  
  // Provide the context value
  const contextValue: LocalStorageContextType = {
    getValue,
    setValue,
    viewMode,
    setViewMode,
    theme,
    setTheme,
    pinSetupStatus,
    setPinSetupStatus,
    clearValue,
    needsPinSetup,
    syncPinSetupStatus
  };
  
  return (
    <LocalStorageContext.Provider value={contextValue}>
      {children}
    </LocalStorageContext.Provider>
  );
};

// Custom hook to use the context
export const useLocalStorageContext = () => {
  const context = useContext(LocalStorageContext);
  if (context === undefined) {
    throw new Error('useLocalStorageContext must be used within a LocalStorageProvider');
  }
  return context;
};
